import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue as mode,
  useDisclosure,
} from "@chakra-ui/react"
import { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { formatAUDate, formatAUTime } from "../global-client"

const categoryOptions = ["Customer", "Prospect", "Unlikely", "Irrelevant", "Spam"]
const datePresetOptions = [
  { value: "custom", label: "Custom" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "thisWeekMonToday", label: "This week (Mon - Today)" },
  { value: "last7Days", label: "Last 7 days" },
  { value: "lastWeekMonSun", label: "Last week (Mon - Sun)" },
  { value: "last14Days", label: "Last 14 days" },
  { value: "thisMonth", label: "This month" },
  { value: "last30Days", label: "Last 30 days" },
  { value: "lastMonth", label: "Last month" },
  { value: "allTime", label: "All time" },
]

const formatDateInput = (date) => {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, "0")
  const dd = String(date.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

const parseDateInput = (value) => {
  if (typeof value !== "string" || !value) {
    return null
  }

  const parts = value.split("-").map((part) => Number(part))
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
    return null
  }

  const [year, month, day] = parts
  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? null : date
}

const startOfDay = (date) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

const endOfDay = (date) => {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

const addDays = (date, amount) => {
  const d = new Date(date)
  d.setDate(d.getDate() + amount)
  return d
}

const startOfWeekMonday = (date) => {
  const d = startOfDay(date)
  const day = d.getDay()
  const shift = day === 0 ? 6 : day - 1
  d.setDate(d.getDate() - shift)
  return d
}

const startOfMonth = (date) => {
  const d = new Date(date)
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

const endOfMonth = (date) => {
  const d = new Date(date)
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)
}

const getPresetRange = (preset) => {
  const now = new Date()

  if (preset === "today") {
    return { start: startOfDay(now), end: endOfDay(now) }
  }

  if (preset === "yesterday") {
    const yesterday = addDays(now, -1)
    return { start: startOfDay(yesterday), end: endOfDay(yesterday) }
  }

  if (preset === "thisWeekMonToday") {
    return { start: startOfWeekMonday(now), end: endOfDay(now) }
  }

  if (preset === "last7Days") {
    return { start: startOfDay(addDays(now, -6)), end: endOfDay(now) }
  }

  if (preset === "lastWeekMonSun") {
    const thisWeekStart = startOfWeekMonday(now)
    const lastWeekStart = addDays(thisWeekStart, -7)
    return { start: startOfDay(lastWeekStart), end: endOfDay(addDays(lastWeekStart, 6)) }
  }

  if (preset === "last14Days") {
    return { start: startOfDay(addDays(now, -13)), end: endOfDay(now) }
  }

  if (preset === "thisMonth") {
    return { start: startOfMonth(now), end: endOfDay(now) }
  }

  if (preset === "last30Days") {
    return { start: startOfDay(addDays(now, -29)), end: endOfDay(now) }
  }

  if (preset === "lastMonth") {
    const thisMonthStart = startOfMonth(now)
    const lastMonthDate = addDays(thisMonthStart, -1)
    return { start: startOfMonth(lastMonthDate), end: endOfMonth(lastMonthDate) }
  }

  if (preset === "allTime") {
    return { start: new Date(2015, 0, 1), end: endOfDay(now) }
  }

  return null
}

const getPresetLabel = (preset) => {
  const match = datePresetOptions.find((option) => option.value === preset)
  return match ? match.label : "Custom"
}

const formatDateChipLabel = (dateFrom, dateTo) => {
  if (!dateFrom || !dateTo) {
    return "Custom"
  }

  const fromDate = parseDateInput(dateFrom)
  const toDate = parseDateInput(dateTo)

  if (!fromDate || !toDate) {
    return "Custom"
  }

  return `${formatAUDate(fromDate, { weekday: false })} - ${formatAUDate(toDate, { weekday: false })}`
}

const formatTimestamp = (value) => {
  if (!value) {
    return "-"
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return "-"
  }

  return `${formatAUDate(date, { weekday: false })} ${formatAUTime(date)}`
}

const getCategoryColorScheme = (category) => {
  if (category === "Spam") {
    return "red"
  }
  if (category === "Prospect") {
    return "green"
  }
  if (category === "Irrelevant") {
    return "orange"
  }
  if (category === "Unlikely") {
    return "yellow"
  }
  if (category === "Customer") {
    return "blue"
  }
  return "gray"
}

const formatTtcDays = (value) => {
  if (!Number.isFinite(value) || value < 0) {
    return ""
  }

  if (value === 0) {
    return "same day"
  }

  return `${value} day${value === 1 ? "" : "s"}`
}

const getTtcColorScheme = (value) => {
  if (!Number.isFinite(value) || value < 0) {
    return "gray"
  }

  if (value <= 2) {
    return "green"
  }

  if (value <= 7) {
    return "yellow"
  }

  return "red"
}

const openCallerEditDialog = (entry) => {
  if (!entry || typeof window === "undefined") {
    return
  }

  window.dispatchEvent(
    new CustomEvent("caller-dialog-open-entry", {
      detail: {
        historyId: entry?.historyId || entry?._id || null,
        callerName: entry?.callerName || "Unknown",
        profileCallerName: entry?.profileCallerName || null,
        callerNumber: entry?.callerNumber || "",
        customerId: entry?.customerId || null,
        customerName: entry?.customerName || null,
        customerFound: Boolean(entry?.customerFound),
        callCategory: entry?.callCategory || null,
        isSpam: Boolean(entry?.isSpam),
        callerNote: entry?.callerNote || null,
        callerNotes: Array.isArray(entry?.callerNotes) ? entry.callerNotes : [],
        timestamp: entry?.timestamp || null,
        lastCallReceivedAt: entry?.lastCallReceivedAt || null,
        lastCallMessage: entry?.lastCallMessage || null,
      },
    })
  )
}

const CallerIdHistoryScreen = () => {
  const { isOpen: isDatePanelOpen, onOpen: openDatePanel, onClose: closeDatePanel } = useDisclosure()
  const initialLast7DaysRange = getPresetRange("last7Days")
  const initialDateFrom = formatDateInput(initialLast7DaysRange?.start || new Date())
  const initialDateTo = formatDateInput(initialLast7DaysRange?.end || new Date())
  const [history, setHistory] = useState([])
  const [deletingHistoryId, setDeletingHistoryId] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [preset, setPreset] = useState("last7Days")
  const [selectedCategories, setSelectedCategories] = useState([])
  const [customerType, setCustomerType] = useState("all")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [dateFrom, setDateFrom] = useState(initialDateFrom)
  const [dateTo, setDateTo] = useState(initialDateTo)
  const [draftPreset, setDraftPreset] = useState("last7Days")
  const [draftDateFrom, setDraftDateFrom] = useState(initialDateFrom)
  const [draftDateTo, setDraftDateTo] = useState(initialDateTo)
  const [meta, setMeta] = useState({
    totalReturned: 0,
    capped: false,
    totalMatched: 0,
    page: 1,
    pageSize: 50,
    totalPages: 1,
    appliedFilters: null,
  })

  const cardBg = mode("white", "gray.800")
  const borderColor = mode("gray.200", "gray.700")
  const tableHeaderBg = mode("gray.50", "gray.700")
  const tableRowHoverBg = mode("gray.100", "gray.600")

  const dateButtonLabel = useMemo(() => {
    const presetLabel = getPresetLabel(preset)
    const rangeLabel = formatDateChipLabel(dateFrom, dateTo)
    return `${presetLabel}: ${rangeLabel}`
  }, [dateFrom, dateTo, preset])

  const syncDraftFromApplied = useCallback(() => {
    setDraftPreset(preset)
    setDraftDateFrom(dateFrom)
    setDraftDateTo(dateTo)
  }, [dateFrom, dateTo, preset])

  const applyDateSelection = useCallback(
    (nextPreset, nextDateFrom, nextDateTo, options = {}) => {
      const shouldClosePanel = options?.closePanel !== false

      setPreset(nextPreset)
      setDateFrom(nextDateFrom)
      setDateTo(nextDateTo)

      if (shouldClosePanel) {
        closeDatePanel()
      }

      if (page !== 1) {
        setPage(1)
      }
    },
    [closeDatePanel, page]
  )

  const handleQuickPresetClick = useCallback(
    (nextPreset) => {
      if (nextPreset === "custom") {
        setDraftPreset("custom")
        return
      }

      const range = getPresetRange(nextPreset)
      if (!range) {
        return
      }

      const nextDateFrom = formatDateInput(range.start)
      const nextDateTo = formatDateInput(range.end)

      setDraftPreset(nextPreset)
      setDraftDateFrom(nextDateFrom)
      setDraftDateTo(nextDateTo)

      applyDateSelection(nextPreset, nextDateFrom, nextDateTo, { closePanel: true })
    },
    [applyDateSelection]
  )

  const loadHistory = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const params = {
        preset: "custom",
        customerType,
        page,
        pageSize,
      }

      if (selectedCategories.length > 0) {
        params.category = selectedCategories.join(",")
      }

      params.dateFrom = dateFrom
      params.dateTo = dateTo

      if (search.trim()) {
        params.search = search.trim()
      }

      const { data } = await axios.get("/api/caller-id/history", { params })

      setHistory(Array.isArray(data?.history) ? data.history : [])
      setMeta({
        totalReturned: data?.totalReturned || 0,
        capped: Boolean(data?.capped),
        totalMatched: data?.totalMatched || 0,
        page: data?.page || page,
        pageSize: data?.pageSize || pageSize,
        totalPages: data?.totalPages || 1,
        appliedFilters: data?.appliedFilters || null,
      })
    } catch (requestError) {
      const msg =
        requestError?.response?.data?.message ||
        requestError?.message ||
        "Failed to load caller ID history"
      setError(msg)
      setHistory([])
      setMeta({
        totalReturned: 0,
        capped: false,
        totalMatched: 0,
        page: 1,
        pageSize,
        totalPages: 1,
        appliedFilters: null,
      })
    } finally {
      setLoading(false)
    }
  }, [selectedCategories, customerType, dateFrom, dateTo, search, page, pageSize])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  useEffect(() => {
    const handleCallerMetaUpdated = () => {
      loadHistory()
    }

    window.addEventListener("caller-meta-updated", handleCallerMetaUpdated)

    return () => {
      window.removeEventListener("caller-meta-updated", handleCallerMetaUpdated)
    }
  }, [loadHistory])

  useEffect(() => {
    const handleIncomingCallerEvent = (event) => {
      if (event?.detail?.reason !== "caller-id") {
        return
      }

      loadHistory()
    }

    window.addEventListener("caller-sse-status", handleIncomingCallerEvent)

    return () => {
      window.removeEventListener("caller-sse-status", handleIncomingCallerEvent)
    }
  }, [loadHistory])

  const summaryText = useMemo(() => {
    const base = `${meta.totalMatched} matching record${meta.totalMatched === 1 ? "" : "s"}`
    if (meta.capped) {
      return `${base}. Results may be truncated due to server scan limits.`
    }
    return base
  }, [meta.capped, meta.totalMatched])

  const categoryFilterLabel = useMemo(() => {
    if (selectedCategories.length === 0) {
      return "All categories"
    }

    if (selectedCategories.length <= 2) {
      return selectedCategories.join(", ")
    }

    return `${selectedCategories.length} categories selected`
  }, [selectedCategories])

  const handleApplyFilters = () => {
    if (page === 1) {
      loadHistory()
      return
    }

    setPage(1)
  }

  const handleApplyDateRange = () => {
    applyDateSelection(draftPreset, draftDateFrom, draftDateTo, { closePanel: true })
  }

  const handleDeleteHistoryEntry = useCallback(
    async (entry) => {
      const historyId = entry?.historyId || entry?._id
      if (!historyId) {
        setError("Unable to delete this row because its history id is missing")
        return
      }

      const confirmed = window.confirm(
        "Delete this caller history entry? This only removes the selected history record and does not change the caller profile."
      )

      if (!confirmed) {
        return
      }

      setDeletingHistoryId(historyId)
      setError(null)

      try {
        await axios.delete(`/api/caller-id/history/${historyId}`)

        setHistory((prev) => prev.filter((row) => (row?.historyId || row?._id) !== historyId))
        loadHistory()
      } catch (requestError) {
        const msg =
          requestError?.response?.data?.message ||
          requestError?.message ||
          "Failed to delete caller history entry"
        setError(msg)
      } finally {
        setDeletingHistoryId("")
      }
    },
    [loadHistory]
  )

  const isDateRangeInvalid = !draftDateFrom || !draftDateTo

  const draftStartDate = parseDateInput(draftDateFrom)
  const draftEndDate = parseDateInput(draftDateTo)

  return (
    <Box maxW='7xl' mx='auto' px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
      <VStack align='stretch' spacing={6}>
        <Box>
          <Flex justify='space-between' align={{ base: "stretch", md: "center" }} gap={3} direction={{ base: "column", md: "row" }} mb={2}>
            <Heading size='lg'>Caller ID History</Heading>
            <Button colorScheme='blue' variant='outline' onClick={loadHistory} isLoading={loading} alignSelf={{ base: "flex-start", md: "auto" }}>
              Refresh
            </Button>
          </Flex>
          <Text color='gray.600' fontSize='sm'>
            Tabular view of incoming calls by date range, category, and customer match
          </Text>
        </Box>

        <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' p={4}>
          <VStack align='stretch' spacing={4}>
            <HStack spacing={4} flexWrap='wrap' align='end'>
              <FormControl maxW='420px' flex='1'>
                <FormLabel fontSize='sm'>Date Range</FormLabel>
                <Popover
                  placement='bottom-start'
                  isOpen={isDatePanelOpen}
                  onOpen={() => {
                    syncDraftFromApplied()
                    openDatePanel()
                  }}
                  onClose={closeDatePanel}
                >
                  <PopoverTrigger>
                    <Button variant='outline' justifyContent='flex-start' width='100%'>
                      {dateButtonLabel}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent width={{ base: "100%", md: "860px" }} maxW='95vw'>
                    <PopoverBody p='0'>
                      <Flex direction={{ base: "column", md: "row" }}>
                        <VStack
                          align='stretch'
                          spacing='0'
                          minW={{ base: "100%", md: "250px" }}
                          borderRightWidth={{ base: "0", md: "1px" }}
                          borderBottomWidth={{ base: "1px", md: "0" }}
                          borderColor={borderColor}
                        >
                          {datePresetOptions.map((option) => (
                            <Button
                              key={option.value}
                              justifyContent='flex-start'
                              variant={draftPreset === option.value ? "solid" : "ghost"}
                              colorScheme={draftPreset === option.value ? "blue" : "gray"}
                              borderRadius='0'
                              h='44px'
                              fontSize='sm'
                              fontWeight='500'
                              _hover={{ bg: draftPreset === option.value ? undefined : mode("gray.100", "gray.700") }}
                              onClick={() => handleQuickPresetClick(option.value)}
                            >
                              {option.label}
                            </Button>
                          ))}
                        </VStack>
                        <Box p='4' flex='1'>
                          <Stack direction={{ base: "column", md: "row" }} spacing={4} mb={4}>
                            <FormControl>
                              <FormLabel fontSize='xs' color='gray.500'>
                                Start date
                              </FormLabel>
                              <Input
                                type='date'
                                value={draftDateFrom}
                                onChange={(e) => {
                                  setDraftPreset("custom")
                                  setDraftDateFrom(e.target.value)
                                }}
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel fontSize='xs' color='gray.500'>
                                End date
                              </FormLabel>
                              <Input
                                type='date'
                                value={draftDateTo}
                                onChange={(e) => {
                                  setDraftPreset("custom")
                                  setDraftDateTo(e.target.value)
                                }}
                              />
                            </FormControl>
                          </Stack>
                          <DatePicker
                            inline
                            monthsShown={2}
                            selectsRange
                            startDate={draftStartDate}
                            endDate={draftEndDate}
                            onChange={(dates) => {
                              const [start, end] = dates
                              setDraftPreset("custom")
                              if (start) {
                                setDraftDateFrom(formatDateInput(start))
                              }
                              if (end) {
                                setDraftDateTo(formatDateInput(end))
                              }
                            }}
                          />
                        </Box>
                      </Flex>
                    </PopoverBody>
                    <Divider />
                    <PopoverFooter>
                      <HStack justify='flex-end'>
                        <Button variant='ghost' onClick={closeDatePanel}>
                          Cancel
                        </Button>
                        <Button colorScheme='blue' onClick={handleApplyDateRange} isDisabled={isDateRangeInvalid}>
                          Apply
                        </Button>
                      </HStack>
                    </PopoverFooter>
                  </PopoverContent>
                </Popover>
              </FormControl>
            </HStack>

            <HStack spacing={4} flexWrap='wrap' align='end'>
              <FormControl minW='280px' flex='1'>
                <FormLabel fontSize='sm'>Category</FormLabel>
                <Menu closeOnSelect={false}>
                  {({ onClose }) => (
                    <>
                      <MenuButton as={Button} variant='outline' width='100%' textAlign='left' fontWeight='normal'>
                        {categoryFilterLabel}
                      </MenuButton>
                      <MenuList minW='280px' maxH='320px' overflowY='auto'>
                        <HStack px='3' py='2' justify='space-between'>
                          <Text fontSize='xs' color='gray.500'>
                            {selectedCategories.length} selected
                          </Text>
                          <Button
                            size='xs'
                            variant='ghost'
                            onClick={() => {
                              setSelectedCategories([])
                              onClose()
                            }}
                            isDisabled={selectedCategories.length === 0}
                          >
                            Clear
                          </Button>
                        </HStack>
                        <Divider my='1' />
                        {categoryOptions.map((option) => {
                          const isChecked = selectedCategories.includes(option)
                          return (
                            <MenuItem
                              key={option}
                              onClick={() => {
                                setSelectedCategories((prev) => {
                                  if (prev.includes(option)) {
                                    return prev.filter((value) => value !== option)
                                  }

                                  return [...prev, option]
                                })
                              }}
                            >
                              <Checkbox isChecked={isChecked} pointerEvents='none'>
                                {option}
                              </Checkbox>
                            </MenuItem>
                          )
                        })}
                      </MenuList>
                    </>
                  )}
                </Menu>
              </FormControl>

              <FormControl maxW='240px'>
                <FormLabel fontSize='sm'>Customer Type</FormLabel>
                <Select value={customerType} onChange={(e) => setCustomerType(e.target.value)}>
                  <option value='all'>All</option>
                  <option value='customer'>Customer</option>
                  <option value='nonCustomer'>Non-customer</option>
                </Select>
              </FormControl>

              <FormControl maxW='300px' flex='1'>
                <FormLabel fontSize='sm'>Search</FormLabel>
                <Input
                  placeholder='Caller Number or Customer Name'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </FormControl>

              <Button colorScheme='blue' onClick={handleApplyFilters} isLoading={loading}>
                Apply
              </Button>
            </HStack>

          </VStack>
        </Box>

        {error && (
          <Alert status='error' borderRadius='md'>
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Text fontSize='sm' color='gray.600'>
          {summaryText}
        </Text>
        <Flex justify='space-between' align='center' gap={4} wrap='wrap'>
          <Text fontSize='sm' color='gray.600'>
            Page {meta.page} of {meta.totalPages} ({meta.totalReturned} shown on this page)
          </Text>

          <HStack spacing={3} align='center'>
            <Text fontSize='sm' fontWeight='medium' whiteSpace='nowrap'>
              Rows per page
            </Text>
            <Select
              maxW='120px'
              size='sm'
              value={String(pageSize)}
              onChange={(e) => {
                const nextPageSize = Number(e.target.value)
                setPage(1)
                setPageSize(nextPageSize)
              }}
            >
              <option value='25'>25</option>
              <option value='50'>50</option>
              <option value='100'>100</option>
            </Select>
          </HStack>
        </Flex>

        <Box bg={cardBg} borderWidth='1px' borderColor={borderColor} borderRadius='lg' shadow='md' overflowX='auto'>
          <Table variant='simple' size='sm'>
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>Received At</Th>
                <Th>Caller Number</Th>
                <Th>Customer</Th>
                <Th>Category</Th>
                <Th>TTC</Th>
                <Th>Note</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={7} textAlign='center' py={8}>
                    <Spinner size='md' />
                  </Td>
                </Tr>
              ) : history.length === 0 ? (
                <Tr>
                  <Td colSpan={7} textAlign='center' py={8}>
                    <Text color='gray.500'>No caller history records found for selected filters</Text>
                  </Td>
                </Tr>
              ) : (
                history.map((entry, index) => (
                  <Tr key={`${entry?.callerNumber || "unknown"}-${entry?.timestamp || index}`} _hover={{ bg: tableRowHoverBg }}>
                    <Td>{formatTimestamp(entry?.timestamp)}</Td>
                    <Td>{entry?.callerNumber || "-"}</Td>
                    <Td>
                      {entry?.customerName ? (
                        entry.customerName
                      ) : entry?.profileCallerName ? (
                        <Text as='span' fontStyle='italic'>
                          {entry.profileCallerName}
                        </Text>
                      ) : (
                        "Not matched"
                      )}
                    </Td>
                    <Td>
                      {entry?.callCategory ? (
                        <Badge colorScheme={getCategoryColorScheme(entry.callCategory)}>{entry.callCategory}</Badge>
                      ) : (
                        <Text color='gray.500'>-</Text>
                      )}
                    </Td>
                    <Td>
                      {Number.isFinite(entry?.ttcDays) && entry.ttcDays >= 0 ? (
                        <Badge colorScheme={getTtcColorScheme(entry.ttcDays)}>{formatTtcDays(entry.ttcDays)}</Badge>
                      ) : (
                        ""
                      )}
                    </Td>
                    <Td maxW='280px' whiteSpace='normal'>
                      {entry?.callerNote || "-"}
                    </Td>
                    <Td>
                      <HStack spacing={2} align='center'>
                        <Button size='xs' colorScheme='blue' variant='outline' onClick={() => openCallerEditDialog(entry)}>
                          Edit
                        </Button>
                        <Button
                          size='xs'
                          colorScheme='red'
                          variant='outline'
                          onClick={() => handleDeleteHistoryEntry(entry)}
                          isLoading={deletingHistoryId === (entry?.historyId || entry?._id)}
                        >
                          Delete
                        </Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        <HStack justify='space-between' flexWrap='wrap'>
          <Button
            variant='outline'
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            isDisabled={loading || meta.page <= 1}
          >
            Previous
          </Button>
          <Button
            variant='outline'
            onClick={() => setPage((prev) => Math.min(meta.totalPages, prev + 1))}
            isDisabled={loading || meta.page >= meta.totalPages}
          >
            Next
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}

export default CallerIdHistoryScreen
